import React, {FC, useCallback, useEffect} from 'react'

import cn from 'classnames/bind'
import Image from 'next/image'
import {useRouter} from 'next/router'

import IconArrowDown from 'assets/icons/angle-left.svg'
import DocumentToolsList from 'components/blocks/DocumentToolsList'
import PortableBlocks from 'components/blocks/PortableBlocks'
import ReplyForm from 'components/blocks/ReplyForm'
import {Col, Container, Row} from 'components/elements/grid'
import ImagePlaceholder from 'components/elements/ImagePlaceholder'
import {TPost} from 'src/types/blog'
import {posthogTrack, getFormattedDate, klaviyoTrack} from 'src/utils'

import s from './TemplateBlogPost.module.scss'

const cx = cn.bind(s)

interface ITemplateBlogPostProps {
	className?: string
	post: TPost | null
}

const TemplateBlogPost: FC<ITemplateBlogPostProps> = ({className, post}) => {
	const router = useRouter()

	useEffect(() => {
		posthogTrack('[BLOG POST ITEM] Page opened')
		klaviyoTrack('Page opened', {page: 'post', href: window?.location?.href})
	}, [])

	const onBack = useCallback(() => router.back(), [router])

	return (
		<div className={cx('wrapper', className)}>
			<Container>
				<div className={cx('back')}>
					<button className={cx('back__btn', 'fs-tab')} onClick={onBack}>
						<IconArrowDown />
						back
					</button>
				</div>
				<Row>
					<Col>
						<div className={cx('top')}>
							<h1 className={cx('post__title', 'top__title', 'h2')}>{post?.title}</h1>
						</div>
					</Col>
				</Row>
				<Row>
					<Col md={{offset: 1, span: 10}} lg={{offset: 2, span: 8}} xl={{offset: 3, span: 6}}>
						<figure className={cx('post__figure')}>
							{typeof post?.mainImage === 'object' && post?.mainImage.url ? (
								<Image
									className={cx('post__main-img')}
									priority
									src={post?.mainImage.url}
									alt={post?.mainImage.alt || ''}
									sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 60vw"
									width={post.mainImage.width}
									height={post.mainImage.height}
								/>
							) : (
								<ImagePlaceholder />
							)}
						</figure>

						{!!post?.publishedAt && (
							<p className={cx('post__published')}>
								<span>{getFormattedDate(new Date(post.publishedAt))}</span>
							</p>
						)}
					</Col>
				</Row>
				<Row>
					<Col lg={{offset: 1, span: 10}} xl={{offset: 2, span: 8}}>
						<div className={cx('post__content')}>
							{post?.body && <PortableBlocks blockContent={post.body} />}
						</div>
					</Col>
				</Row>
				<Row>
					<Col
						className={cx('reply')}
						md={{offset: 1, span: 10}}
						lg={{offset: 2, span: 8}}
						xl={{offset: 3, span: 6}}>
						<ReplyForm />
					</Col>
				</Row>
			</Container>

			<DocumentToolsList />
		</div>
	)
}

TemplateBlogPost.displayName = 'TemplateBlogPost'

export default TemplateBlogPost
